"use client";

import { apiUpdatePushToken } from "@/api/api";
import { useAuthStore } from "@/store/auth";
import { getFCMTokenFromApp } from "@/utils/fcm-token";
import { inApp } from "@/utils/interface";
import { useEffect, useRef } from "react";

/**
 * 用户登录成功后更新推送Token的Hook
 *
 * 工作流程:
 * 1. 监听用户token变化(登录成功)
 * 2. 等待axios拦截器注入token到header
 * 3. 从App JSBridge异步获取FCM Token
 * 4. 调用API更新推送Token到服务器
 *
 * 注意: 只在App环境中执行
 */
export function usePushTokenUpdate() {
    const token = useAuthStore( state => state.token );
    const hasUpdatedRef = useRef( false );
    const isUpdatingRef = useRef( false );

    useEffect( () => {
        // 只在App环境中执行
        if ( !inApp() ) {
            console.log( "[PushToken] 非App环境, 跳过推送Token更新" );
            return;
        }

        // 未登录或已更新过,跳过
        if ( !token ) {
            console.log( "[PushToken] token为空, 跳过" );
            return;
        }
        if ( hasUpdatedRef.current ) {
            console.log( "[PushToken] 已更新过, 跳过" );
            return;
        }
        if ( isUpdatingRef.current ) {
            console.log( "[PushToken] 正在更新中, 跳过" );
            return;
        }

        const updatePushToken = async () => {
            console.log( "[调试] updatePushToken 函数开始执行" );
            console.log( "[调试] token:", token );
            isUpdatingRef.current = true;
            console.log( "[PushToken] 开始更新, 当前token:", token );

            try {
                // 1. 等待一小段时间确保axios拦截器已将token注入到header
                await new Promise( resolve => setTimeout( resolve, 100 ) );
                console.log( "[PushToken] 等待header注入完成" );

                // 2. 从App获取FCM Token
                let fcmToken = "";
                try {
                    fcmToken = await getFCMTokenFromApp();
                    console.log( "[PushToken] 获取到FCM Token:", fcmToken );
                } catch ( err ) {
                    console.error( "[PushToken] 获取FCM Token失败", err );
                    throw err;
                }

                // 3. 调用API上传到服务器
                const [ isOk, res ] = await apiUpdatePushToken( { pushToken: fcmToken } );
                console.log( "[PushToken] API返回:", isOk, res );

                if ( isOk ) {
                    hasUpdatedRef.current = true;
                    console.log( "[PushToken] 推送Token上传成功" );
                } else {
                    console.error( "[PushToken] 推送Token上传失败:", res );
                }
            } catch ( error ) {
                console.error( "[PushToken] 更新推送Token异常:", error );
            } finally {
                isUpdatingRef.current = false;
            }
        };

        updatePushToken();
    }, [ token ] );
}
